import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Mail } from 'lucide-react';
import { usePatchJobMutation } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import { isJobLocked } from '../../utils/lockHelpers';
import { formatDateForInput } from '../../utils/jobDateValidation';
import type { JobFormData, JobLockData, JobSummary } from '../../utils/types';

const DATE_INPUT_CLASS =
  'form-input text-sm w-full min-w-[140px] border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed';

interface PalletDeliveryDateEditorProps {
  job: Pick<
    JobSummary,
    | 'id'
    | 'website_job_number'
    | 'pallet_delivery_date'
    | 'pallet_delivery_email_sent_at'
    | 'pallet_delivery_email_sent_by_username'
    | 'lock_data'
  >;
  onUpdated?: () => void;
}

export const PalletDeliveryDateEditor: React.FC<PalletDeliveryDateEditorProps> = ({
  job,
  onUpdated,
}) => {
  const [patchJob, { isLoading }] = usePatchJobMutation();
  const toast = useToast();
  const locked = isJobLocked(job.lock_data as JobLockData | null | undefined);

  const savedInputValue = formatDateForInput(job.pallet_delivery_date);
  const [localValue, setLocalValue] = useState(savedInputValue);

  useEffect(() => {
    setLocalValue(savedInputValue);
  }, [savedInputValue, job.id]);

  const handleChange = async (newValue: string) => {
    if (newValue === savedInputValue) return;
    if (locked) {
      toast.warning('Locked', 'This job is locked and the pallet delivery date cannot be changed');
      setLocalValue(savedInputValue);
      return;
    }
    try {
      await patchJob({
        id: job.id,
        data: { pallet_delivery_date: newValue || null } as Partial<JobFormData>,
      }).unwrap();
      toast.success('Saved', 'Pallet delivery date updated');
      onUpdated?.();
    } catch (e: unknown) {
      setLocalValue(savedInputValue);
      const message =
        e && typeof e === 'object' && 'data' in e
          ? (e as { data?: { detail?: string } }).data?.detail
          : undefined;
      toast.error('Update failed', message || 'Could not save pallet delivery date');
    }
  };

  const emailSentLabel =
    job.pallet_delivery_email_sent_at != null
      ? `${format(new Date(job.pallet_delivery_email_sent_at), 'dd/MM/yyyy HH:mm')}${
          job.pallet_delivery_email_sent_by_username
            ? ` by ${job.pallet_delivery_email_sent_by_username}`
            : ''
        }`
      : null;

  return (
    <div className="flex flex-col gap-1.5 w-full min-w-[10rem]">
      <label className="text-xs font-medium text-gray-600">Pallet delivery date</label>
      <input
        type="date"
        value={localValue}
        disabled={locked || isLoading}
        title={locked ? 'Job is locked' : undefined}
        className={DATE_INPUT_CLASS}
        onChange={(e) => {
          setLocalValue(e.target.value);
          void handleChange(e.target.value);
        }}
      />
      {emailSentLabel && (
        <p className="text-xs text-green-800 flex items-start gap-1">
          <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Email sent {emailSentLabel}
          </span>
        </p>
      )}
    </div>
  );
};
